import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Loader2, Download, Filter, Star } from 'lucide-react';
import { listAdminSurveys } from '../services/adminApi';

function csvEscape(value) {
    if (value == null) return '';
    const s = String(value).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
}

const UEQ_KEYS = [
    'obstructive_supportive', 'complicated_easy', 'inefficient_efficient', 'confusing_clear',
    'boring_exciting', 'notInteresting_interesting', 'conventional_innovative', 'unattractive_attractive',
];
const LIKERT_KEYS = ['realism', 'feedback_useful', 'comfort', 'tone', 'trust'];

function avg(values) {
    const nums = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (!nums.length) return null;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function downloadCsv(rows, filename = 'surveys.csv') {
    const cols = [
        'submitted_at', 'email',
        'age_range', 'country', 'role', 'seniority', 'sectors',
        'ueq_avg', ...UEQ_KEYS.map((k) => `ueq_${k}`),
        'likert_avg', ...LIKERT_KEYS.map((k) => `likert_${k}`),
        'scenario_nombre', 'tipo_proyecto',
        'habilidades', 'nivel', 'progress',
        'open_text', 'client_id', 'thread_id', 'locale',
    ];
    const head = cols.join(',');
    const body = rows.map(r => cols.map(c => csvEscape(r[c])).join(',')).join('\n');
    const csv = `${head}\n${body}`;
    const blob = new Blob(["﻿" + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function flattenSurvey(s) {
    const meta = s.metadata || {};
    // Fallback a metadata por si el backend descarta los campos nuevos en la
    // raíz (schema estricto). Tanto FeedbackSurvey como adminApi duplican
    // ueq/likert/demographics/open_text dentro de `metadata`.
    const ueq = s.ueq || meta.ueq || {};
    const likert = s.likert || meta.likert || s.ratings || {};
    const demo = s.demographics || meta.demographics || {};
    const ueqAvg = avg(UEQ_KEYS.map((k) => ueq[k]));
    const likertAvg = avg(LIKERT_KEYS.map((k) => likert[k]));
    const row = {
        submitted_at: s.submitted_at,
        email: s.email || '',
        age_range: demo.age_range || '',
        country: demo.country || '',
        role: demo.role || '',
        seniority: demo.seniority || '',
        sectors: Array.isArray(demo.sectors) ? demo.sectors.join(' / ') : '',
        ueq_avg: ueqAvg != null ? ueqAvg.toFixed(2) : '',
        likert_avg: likertAvg != null ? likertAvg.toFixed(2) : '',
        scenario_nombre: meta.scenario_nombre || meta.scenario_slug || '',
        tipo_proyecto: meta.tipo_proyecto || '',
        habilidades: Array.isArray(meta.habilidad_nombres) ? meta.habilidad_nombres.join(' / ')
            : Array.isArray(meta.habilidades) ? meta.habilidades.join(' / ')
            : '',
        nivel: meta.nivel ?? '',
        progress: meta.progress != null ? Math.round(Math.max(0, Math.min(1, meta.progress)) * 100) + '%' : '',
        open_text: s.open_text || meta.open_text || s.comment || '',
        client_id: s.client_id || '',
        thread_id: s.thread_id || '',
        locale: s.locale || '',
        blob_path: s.blob_path || '',
    };
    UEQ_KEYS.forEach((k) => { row[`ueq_${k}`] = ueq[k] ?? ''; });
    LIKERT_KEYS.forEach((k) => { row[`likert_${k}`] = likert[k] ?? ''; });
    return row;
}

function StarsCell({ value, max = 5 }) {
    if (value == null || value === '') return <span className="text-slate-500">—</span>;
    const num = Number(value);
    if (Number.isNaN(num)) return <span className="text-slate-500">—</span>;
    return (
        <span className="inline-flex items-center gap-0.5 text-amber-300">
            {Array.from({ length: max }).map((_, i) => (
                <Star
                    key={i}
                    className="w-3.5 h-3.5"
                    style={{ fill: i < num ? '#fbbf24' : 'transparent', color: i < num ? '#fbbf24' : '#475569' }}
                />
            ))}
            <span className="ml-1 text-slate-300 text-xs">{num}</span>
        </span>
    );
}

function ScoreCell({ value, scale = '/5' }) {
    if (value == null || value === '') return <span className="text-slate-500">—</span>;
    return (
        <span className="text-slate-200 text-xs font-medium">
            {value}<span className="text-slate-500"> {scale}</span>
        </span>
    );
}

export default function AdminSurveysTable() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({ from: '', to: '' });
    const [search, setSearch] = useState('');

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listAdminSurveys({
                from: filters.from || undefined,
                to: filters.to || undefined,
                limit: 1000,
            });
            setItems(res.items || []);
        } catch (err) {
            setError(err.message || 'Error cargando encuestas');
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

    const flat = useMemo(() => items.map(flattenSurvey), [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return flat;
        return flat.filter(r =>
            (r.email || '').toLowerCase().includes(q)
            || (r.scenario_nombre || '').toLowerCase().includes(q)
            || (r.habilidades || '').toLowerCase().includes(q)
            || (r.open_text || '').toLowerCase().includes(q)
            || (r.client_id || '').toLowerCase().includes(q)
            || (r.country || '').toLowerCase().includes(q)
            || (r.role || '').toLowerCase().includes(q)
            || (r.sectors || '').toLowerCase().includes(q)
        );
    }, [flat, search]);

    const stats = useMemo(() => {
        const ueqAvgs = filtered.map(r => parseFloat(r.ueq_avg)).filter(v => !Number.isNaN(v));
        const likertAvgs = filtered.map(r => parseFloat(r.likert_avg)).filter(v => !Number.isNaN(v));
        const meanUeq = ueqAvgs.length ? (ueqAvgs.reduce((a, b) => a + b, 0) / ueqAvgs.length) : null;
        const meanLikert = likertAvgs.length ? (likertAvgs.reduce((a, b) => a + b, 0) / likertAvgs.length) : null;
        const withEmail = filtered.filter(r => r.email).length;
        const uniqueClients = new Set(filtered.map(r => r.client_id).filter(Boolean)).size;
        return { count: filtered.length, meanUeq, meanLikert, withEmail, uniqueClients };
    }, [filtered]);

    return (
        <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label="Total" value={stats.count} />
                <Stat label="UEQ promedio (1–7)" value={stats.meanUeq ? stats.meanUeq.toFixed(2) : '—'} />
                <Stat label="Likert promedio (1–5)" value={stats.meanLikert ? stats.meanLikert.toFixed(2) : '—'} />
                <Stat label="Con email" value={stats.withEmail} />
                <Stat label="Usuarios únicos" value={stats.uniqueClients} />
            </div>

            {/* Toolbar */}
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-300">
                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                    <span>Desde</span>
                    <input
                        type="date"
                        value={filters.from}
                        onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))}
                        className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-white"
                    />
                    <span>hasta</span>
                    <input
                        type="date"
                        value={filters.to}
                        onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
                        className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-white"
                    />
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="ml-1 flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-slate-700/60 hover:bg-slate-600 text-white disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Aplicar
                    </button>
                </div>

                <input
                    type="text"
                    placeholder="Buscar (email, escenario, comentario)…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 min-w-[180px] bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-white placeholder-slate-500"
                />

                <button
                    onClick={() => downloadCsv(filtered, `surveys-${new Date().toISOString().slice(0, 10)}.csv`)}
                    disabled={!filtered.length}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold disabled:opacity-40"
                >
                    <Download className="w-3.5 h-3.5" />
                    CSV
                </button>
            </div>

            {error && (
                <div className="text-sm text-red-300 bg-red-900/30 border border-red-700/40 rounded-lg px-3 py-2">
                    {error}
                </div>
            )}

            {/* Tabla — scroll interno (la barra horizontal queda dentro del card) */}
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl overflow-hidden">
                <div className="overflow-auto max-h-[55vh] min-h-[260px]">
                    <table className="min-w-full text-xs">
                        <thead className="text-slate-300 uppercase tracking-wider sticky top-0 z-20">
                            <tr>
                                <Th sticky>Fecha</Th>
                                <Th sticky>Email</Th>
                                <Th sticky>Edad</Th>
                                <Th sticky>País</Th>
                                <Th sticky>Rol</Th>
                                <Th sticky>Seniority</Th>
                                <Th sticky>Sectores</Th>
                                <Th sticky>UEQ avg</Th>
                                <Th sticky>Likert avg</Th>
                                <Th sticky>Realismo</Th>
                                <Th sticky>Feedback útil</Th>
                                <Th sticky>Confianza</Th>
                                <Th sticky>Escenario</Th>
                                <Th sticky>Habilidad</Th>
                                <Th sticky>Nivel</Th>
                                <Th sticky>Texto abierto</Th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {loading && !filtered.length ? (
                                <tr><td colSpan={16} className="text-center py-6 text-slate-400">
                                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando…
                                </td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={16} className="text-center py-6 text-slate-500">Sin resultados</td></tr>
                            ) : filtered.map((r, i) => (
                                <tr key={r.blob_path || i} className="hover:bg-slate-800/40">
                                    <Td className="whitespace-nowrap text-slate-400">
                                        {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
                                    </Td>
                                    <Td className="text-slate-200">{r.email || <span className="text-slate-500">—</span>}</Td>
                                    <Td className="text-slate-300 whitespace-nowrap">{r.age_range || <span className="text-slate-500">—</span>}</Td>
                                    <Td className="text-slate-300 whitespace-nowrap max-w-[120px] truncate" title={r.country}>{r.country || <span className="text-slate-500">—</span>}</Td>
                                    <Td className="text-slate-300 max-w-[140px] truncate" title={r.role}>{r.role || <span className="text-slate-500">—</span>}</Td>
                                    <Td className="text-slate-400 capitalize whitespace-nowrap">{r.seniority || <span className="text-slate-500">—</span>}</Td>
                                    <Td className="text-slate-300 max-w-[180px] truncate" title={r.sectors}>{r.sectors || <span className="text-slate-500">—</span>}</Td>
                                    <Td><ScoreCell value={r.ueq_avg} scale="/7" /></Td>
                                    <Td><ScoreCell value={r.likert_avg} scale="/5" /></Td>
                                    <Td><StarsCell value={r.likert_realism} /></Td>
                                    <Td><StarsCell value={r.likert_feedback_useful} /></Td>
                                    <Td><StarsCell value={r.likert_trust} /></Td>
                                    <Td className="text-slate-300 max-w-[200px] truncate" title={r.scenario_nombre}>
                                        {r.scenario_nombre || '—'}
                                    </Td>
                                    <Td className="text-slate-300 max-w-[160px] truncate" title={r.habilidades}>
                                        {r.habilidades || '—'}
                                    </Td>
                                    <Td className="text-slate-400">{r.nivel || '—'}</Td>
                                    <Td className="text-slate-300 max-w-[280px]">
                                        <div className="line-clamp-2" title={r.open_text}>
                                            {r.open_text || <span className="text-slate-500">—</span>}
                                        </div>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-xl font-bold text-white mt-0.5">{value}</p>
        </div>
    );
}

function Th({ children, sticky = false }) {
    const cls = sticky
        ? 'text-left font-semibold px-3 py-2 whitespace-nowrap bg-slate-800/95 backdrop-blur'
        : 'text-left font-semibold px-3 py-2 whitespace-nowrap';
    return <th className={cls}>{children}</th>;
}

function Td({ children, className = '' }) {
    return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
